import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@shared/hooks/use-toast';
import { CreateEntryFormData, transformFormDataToUpdateAPI } from '../../CreateEdit/models/CreateEntrySchema';
import { useUpdateEntry } from './useUpdateEntry';

interface UseEditEntryProps {
  /** ID de la entrada a editar */
  entryId: number;
  /** Instancia del formulario react-hook-form */
  form: UseFormReturn<CreateEntryFormData>;
  /** Callback para resetear productos seleccionados */
  onResetSelectedProducts: () => void;
}

interface UseEditEntryReturn {
  /** Función para enviar el formulario */
  onSubmit: (data: CreateEntryFormData) => void;
  /** Función para cancelar y volver */
  onCancel: () => void;
  /** Estado de carga */
  isLoading: boolean;
  /** Error del envío si existe */
  error: Error | null;
}

/**
 * Custom hook para manejar la edición de entrada
 */
export const useEditEntry = ({ entryId, form, onResetSelectedProducts }: UseEditEntryProps): UseEditEntryReturn => {
  const { t } = useTranslation('merchandiseV3');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { mutate: updateEntry, isPending: isLoading, error } = useUpdateEntry({ entryId });
  const { reset } = form;

  // Handler para cancelar - resetea todo y navega de vuelta
  const onCancel = useCallback(() => {
    reset();
    onResetSelectedProducts();
    navigate('/merchandise');
  }, [reset, onResetSelectedProducts, navigate]);

  // Handler para enviar el formulario
  const onSubmit = useCallback(
    (data: CreateEntryFormData) => {
      // Validar que hay productos seleccionados (usar data del formulario, no el estado externo)
      if (!data.products.length) {
        toast({
          title: t('messages.no_products'),
          description: t('messages.no_products_description'),
          variant: 'destructive',
        });
        return;
      }

      // Transformar datos del formulario al formato para actualización
      const updateData = transformFormDataToUpdateAPI(data);

      // Enviar la actualización
      updateEntry(updateData, {
        onSuccess: () => {
          // Mostrar toast de éxito
          toast({
            title: t('edit.messages.update_success', 'Entrada actualizada'),
            description: t('edit.messages.update_success_description', 'La entrada se ha actualizado correctamente'),
            variant: 'default',
          });

          // Resetear formulario y estado
          reset();
          onResetSelectedProducts();

          // Navegar de vuelta a la lista
          navigate('/merchandise');
        },
        onError: (error: any) => {
          // Extraer mensaje de error de la API
          let errorMessage = t('errors.unexpected', 'Error inesperado');

          // Prioridad 1: Error con estructura { error: { message: "..." } }
          if (error?.response?.data?.error?.message) {
            errorMessage = error.response.data.error.message;
          }
          // Prioridad 2: Error con estructura { message: "..." }
          else if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
          }
          // Prioridad 3: Error genérico
          else if (error?.message) {
            errorMessage = error.message;
          }

          // Mostrar toast con el mensaje de error
          toast({
            title: t('edit.messages.update_error', 'Error al actualizar'),
            description: errorMessage,
            variant: 'destructive',
          });
        },
      });
    },
    [entryId, updateEntry, reset, onResetSelectedProducts, navigate, toast, t],
  );

  return {
    onSubmit,
    onCancel,
    isLoading,
    error,
  };
};
